import Vue from 'vue';
import moment from 'moment';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { swiper, SwiperComponent, swiperSlide } from 'vue-awesome-swiper';
import { SwiperOptions } from 'swiper';

import { CustomerProfile, Promotion, Reward, TokenizedPaymentCard } from '@/models';
import { LoyaltyProfile } from '@/models';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { CountryCode } from '@/store/ui';
import { getCountryName } from '@/helpers';
import { isAutoSubscribesToMarketingEmail } from '@/helpers/country';

@Component({
  name: 'CmgLoyaltyRewardsModal',
  components: {
    swiper,
    swiperSlide,
  },
})
export default class CmgLoyaltyRewardsModal extends Vue {
  swiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 30,
    pagination: {
      el: '.swiper-pagination',
    },
  };

  @Prop({ required: false, default: null, type: Object })
  member?: LoyaltyProfile;

  @Prop({ required: true, type: Array })
  promotions!: Promotion[];

  @Prop({ required: false, default: null, type: Object })
  selectedReward?: Promotion;

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.rewards.visible;
  }

  // TODO Tech Debt, Unit test this
  get sortedPromotions(): Promotion[] {
    return this.promotions.sort((l, r) => moment(r.expirationDate).isBefore(moment(l.expirationDate)) ? -1 : 1);
  }

  get shouldShowRewards(): boolean {
    return !!this.promotions.length;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isGiftCardEnabled(): boolean {
    // TODO: Possibly use countries configuration in constants.ts to drive specific functionality availability and avoid hardcoded country code checks
    return this.countryCode === CountryCode.US;
  }

  get rewardToastVisible() {
    return this.$appStore.state.ui.bag.bagToastVisibility;
  }

  get isCustomerStudent(): boolean {
    return this.$appStore.getters.isActiveStudent;
  }
  
  get chipotleUMobileScanBackground(): string {
    return this.$appStore.state.cms.images.loyalty.chipotleUMobileScanBackground.default;
  }

  get hasValidGiftCard(): boolean {
    return this.isGiftCardEnabled && this.$appStore.state.customer.wallet.paymentCards.some(card => card.isGiftCard === true);
  }

  get medallionFlutedReward(): string {
    return this.$appStore.state.cms.images.logos.medallionFluted.default;
  }

  get scanExplanationText(): string {
    if (this.hasValidGiftCard) {
      return '';
    } else {
      return 'Limit one per order.';
    }
  }

  loyaltyString(): string {
    switch (this.countryCode) {
      case CountryCode.GB:
        return 'offer';
      default:
        return 'reward';
    }
  }

  get countryUseText(): string {
    return `For use in ${getCountryName(this.countryCode, true)} only.`;
  }

  get args() {
    return this.$appStore.state.ui.modals.rewards.args
  }

  get selectedPromotion(): Promotion | null {
    return (this.args && this.args.selectedReward) || null;
  }

  get subHeaderText(): string {
    const isMember = this.member;
    const hasPromotions = this.sortedPromotions.length;

    if (isMember && hasPromotions) {
      return 'One scan to use rewards & earn points.';
    } else if (isMember) {
      return 'To earn points.';
    } else if (hasPromotions) {
      return `To use ${this.loyaltyString()}.`;
    }

    return '';
  }

  get bagOffers(): Reward[] {
    return this.$appStore.state.reward.bagRewards;
  }

  get rewardName() {
    return this.sortedPromotions[0].promotionName || 'Free Entree'
  }

  @Watch('visible', { immediate: false })
  async onVisible(newVisible: boolean) {
    if (newVisible === true) {
      await this.$services.customer.refreshCustomerProfileInfo();
      await this.$services.reward.getBagOffers();
    }
  }

  @Watch('sortedPromotions', { immediate: true })
  async onSortedPromotions(promos: Promotion[]) {
    const swiperRef = this.$refs.rewardsSwiper as SwiperComponent | undefined;

    if (this.selectedPromotion && swiperRef) {
      let index = promos.findIndex(i => i.promotionCode == this.selectedPromotion?.promotionCode)
      swiperRef.swiper?.slideTo(index);
    }
  }

  onClose() {
    this.$services.ui.setModalVisibility('rewards', false);
  }

  openGiftCardQR() {
    this.$services.ui.setModalVisibility('giftCardQr', true);
    this.$services.ui.setModalVisibility('rewards', false);
  }

  onClickJoinRewards() {
    const shouldSubscribeToMarketingEmail = isAutoSubscribesToMarketingEmail(this.customer?.country);
    const enrollmentOptions: EnrollmentOptions = { customer: this.customer, pageTitle: document.title, shouldSubscribeToMarketingEmail };
    this.$services.loyalty.enrollInLoyalty(enrollmentOptions)
      .catch(e => this.$services.logger.error('Something went wrong joining rewards', { error: e }));
  }

  async onRedeemed(): Promise<void> {
    await this.$services.promotions.refreshCustomerPromotions();

    this.$nextTick(() => {
      (this.$refs.rewardSwiper as any)?.swiper?.slideNext();
    });
  }
}
